"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.exportReportExcel = exports.generateReportPDF = exports.previewReportHTML = exports.generatePDFFromHTML = exports.previewHTMLInNewWindow = exports.renderTemplateWithData = exports.PRINT_PAGE_BREAK_CSS = exports.bindCategoryLabelsToTables = exports.fetchTemplateWithData = exports.setGradeReportConfig = void 0;
const mustache_1 = __importDefault(require("mustache"));
const config_1 = require("./config");
var config_2 = require("./config");
Object.defineProperty(exports, "setGradeReportConfig", { enumerable: true, get: function () { return config_2.setConfig; } });
/**
 * Get authentication headers for API requests
 * Checks both 'currentUser' (iteach) and 'user' (iLearn) localStorage keys
 */
const getAuthHeaders = () => {
    let token = '';
    // Try iteach key first, then iLearn key
    const raw = localStorage.getItem('currentUser') || localStorage.getItem('user');
    if (raw && raw !== 'undefined') {
        try {
            const parsed = JSON.parse(raw);
            token = parsed.token || '';
        }
        catch (error) {
            console.error('Failed to parse auth from localStorage:', error);
        }
    }
    return {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    };
};
/**
 * Fetch test tag mappings from backend (for School Report Card categorization)
 */
const fetchTestTagMappings = async () => {
    try {
        const url = `${(0, config_1.getConfig)().nodeSiteUrl}v1/reports/test-tag-mappings`;
        console.log('[fetchTestTagMappings] Fetching test tag mappings');
        const response = await fetch(url, {
            method: 'GET',
            headers: getAuthHeaders(),
        });
        if (!response.ok) {
            console.error('[fetchTestTagMappings] Failed to fetch:', response.statusText);
            return null;
        }
        const result = await response.json();
        if (!result.success || !result.data) {
            console.error('[fetchTestTagMappings] Invalid response');
            return null;
        }
        console.log('[fetchTestTagMappings] Fetched mappings:', result.summary);
        return result.data;
    }
    catch (error) {
        console.error('[fetchTestTagMappings] Error:', error);
        return null;
    }
};
/**
 * Fetch template HTML and student data from backend.
 *
 * `refreshRunId` (optional) pins this dossier to a specific materialization
 * run — captured client-side from the academic-details response and echoed
 * back here so the server can stamp the exact run id onto report_audit and
 * onto the rendered footer.
 * `outputType` tells the server which artifact this call is producing so
 * the audit row is labeled correctly (PREVIEW_HTML or PDF — for EXCEL,
 * use exportReportExcel below).
 */
const fetchTemplateWithData = async (templateId, students, filters, debugMode, refreshRunId, outputType) => {
    try {
        const url = `${(0, config_1.getConfig)().nodeSiteUrl}v1/reports/get-template-with-data`;
        console.log('[fetchTemplateWithData] Fetching template:', templateId);
        // Fetch tag mappings for School Report Card (templateId 4)
        let tagMappings = null;
        if (templateId === 4) {
            console.log('[fetchTemplateWithData] Fetching tag mappings for School Report Card');
            tagMappings = await fetchTestTagMappings();
            if (tagMappings) {
                console.log('[fetchTemplateWithData] Tag mappings fetched successfully');
                console.log('[fetchTemplateWithData] Number of tests with tags:', Object.keys(tagMappings).length);
                console.log('[fetchTemplateWithData] Sample tag mapping:', Object.entries(tagMappings)[0]);
            }
            else {
                console.warn('[fetchTemplateWithData] No tag mappings found, backend will auto-fetch');
            }
        }
        // Build request payload
        const requestPayload = {
            templateId,
            students,
            ...(filters && { filters }),
            ...(debugMode && { debugMode: true }),
            ...(typeof refreshRunId === 'number' && { refreshRunId }),
            ...(outputType && { outputType })
        };
        // Add tagMappings if available
        if (tagMappings) {
            requestPayload.tagMappings = tagMappings;
            console.log('[fetchTemplateWithData] ✅ ADDING tagMappings to payload');
        }
        else {
            console.log('[fetchTemplateWithData] ❌ NO tagMappings to add');
        }
        console.log('[fetchTemplateWithData] Final payload keys:', Object.keys(requestPayload));
        const response = await fetch(url, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(requestPayload)
        });
        if (!response.ok) {
            const errorText = await response.text();
            console.error('[fetchTemplateWithData] Error response:', errorText);
            throw new Error(`Failed to fetch template: ${response.statusText}`);
        }
        const result = await response.json();
        console.log('[fetchTemplateWithData] Response:', result);
        if (!result.success || !result.data) {
            throw new Error('Invalid response from server');
        }
        return {
            templateHtml: result.data.templateHtml,
            students: result.data.students,
            templateName: result.data.templateName,
            batchStatistics: result.data.batchStatistics,
            batchMetadata: result.data.batchMetadata,
            isBatchReport: result.data.metadata?.isBatchReport || false,
            analyticsData: result.data.analyticsData,
        };
    }
    catch (error) {
        console.error('[fetchTemplateWithData] Error:', error);
        return null;
    }
};
exports.fetchTemplateWithData = fetchTemplateWithData;
/**
 * Transform student data into flat structure expected by templates
 */
const transformStudentData = (student) => {
    // Check if student data is already enriched by backend
    // Look for: 'tests' array (PTM/Dossier), 'subjects' array (School/IB Report Card),
    // 'literacySkills' (Pre-Primary), 'gradeBoundaries' (IB), or 'languageSubjects' (PU Board Marks Card)
    const hasTests = student.tests && Array.isArray(student.tests) && student.tests.length > 0;
    const hasSubjects = student.subjects && Array.isArray(student.subjects);
    const hasLiteracySkills = student.literacySkills && Array.isArray(student.literacySkills) && student.literacySkills.length > 0;
    const hasGradeBoundaries = student.gradeBoundaries && Array.isArray(student.gradeBoundaries);
    const hasLanguageSubjects = student.languageSubjects && Array.isArray(student.languageSubjects);
    const isEnriched = hasTests || hasSubjects || hasLiteracySkills || hasGradeBoundaries || hasLanguageSubjects;
    if (isEnriched) {
        // Student is already enriched by backend - return as-is with minimal additions
        console.log('[transformStudentData] Student already enriched, using backend data. Has tests:', hasTests, 'Has subjects:', hasSubjects, 'Has literacySkills:', hasLiteracySkills);
        return {
            ...student,
            // Add any missing helper fields
            currentDate: student.currentDate || new Date().toLocaleDateString(),
            currentYear: student.currentYear || new Date().getFullYear(),
            // Keep original structure
            _raw: student
        };
    }
    // Legacy path: transform raw student data (for backwards compatibility)
    console.log('[transformStudentData] Transforming raw student data');
    const studentInfo = student.StudentInfo || {};
    const testInfo = student.TestInfo || [];
    const attendanceInfo = student.AttendanceInfo || [];
    // Calculate aggregate test scores
    const totalTestsScored = testInfo.reduce((sum, test) => sum + (test.totalScored || 0), 0);
    const totalTestsMaxMarks = testInfo.reduce((sum, test) => sum + (test.totalMaxMarks || 0), 0);
    const overallPercentage = totalTestsMaxMarks > 0 ? ((totalTestsScored / totalTestsMaxMarks) * 100).toFixed(2) : 0;
    // Calculate attendance
    const totalPresents = attendanceInfo.reduce((sum, att) => sum + (att.Presents || 0), 0);
    const totalAbsents = attendanceInfo.reduce((sum, att) => sum + (att.Absents || 0), 0);
    const totalClasses = totalPresents + totalAbsents;
    const attendancePercentage = totalClasses > 0 ? ((totalPresents / totalClasses) * 100).toFixed(2) : 0;
    // Flatten student info and add computed fields
    return {
        // Student basic info (flat)
        studentId: student.Id || 'N/A',
        studentName: studentInfo.FullName || 'N/A',
        firstName: studentInfo.FirstName || 'N/A',
        lastName: studentInfo.LastName || 'N/A',
        email: studentInfo.Email || 'N/A',
        mobile: studentInfo.Mobile || 'N/A',
        enrollmentId: student.EnrollmentId || 'N/A',
        // Class/Batch info
        className: studentInfo.BatchName || 'N/A',
        batchName: studentInfo.BatchName || 'N/A',
        batchCode: studentInfo.BatchCode || 'N/A',
        courseName: studentInfo.CourseName || 'N/A',
        courseCode: studentInfo.CourseCode || 'N/A',
        programId: studentInfo.ProgramId || 'N/A',
        programName: studentInfo.ProgramName || 'N/A',
        programCode: studentInfo.ProgramCode || 'N/A',
        schoolName: studentInfo.SchoolName || 'N/A',
        schoolCode: studentInfo.SchoolCode || 'N/A',
        yearName: studentInfo.YearName || 'N/A',
        // Academic performance
        overallPercentage,
        totalTestsScored,
        totalTestsMaxMarks,
        averageScore: student.averageScore || overallPercentage,
        rank: student.rank || 'N/A',
        // Attendance
        attendancePercentage,
        totalPresents,
        totalAbsents,
        totalClasses,
        // Day-level attendance scalars (passed through from backend's
        // computeOverallAttendanceDays in get-template-with-data.controller.ts)
        totalDaysHeld: student.totalDaysHeld,
        presentsInDays: student.presentsInDays,
        absentsInDays: student.absentsInDays,
        overallAttendancePercent: student.overallAttendancePercent,
        // Test details (array)
        tests: testInfo.map((test) => ({
            testId: test.testId,
            testName: test.testName || 'N/A',
            testCategory: test.testCategoryName || 'N/A',
            scored: test.totalScored || 0,
            maxMarks: test.totalMaxMarks || 0,
            percentage: test.totalMaxMarks > 0 ? ((test.totalScored / test.totalMaxMarks) * 100).toFixed(2) : 0,
            sections: test.sections || []
        })),
        // Attendance details (array)
        attendance: attendanceInfo.map((att) => ({
            month: att.Month || 'N/A',
            year: att.Year || 'N/A',
            presents: att.Presents || 0,
            absents: att.Absents || 0,
            totalClasses: att.TotalClasses || 0,
            percentage: att.TotalClasses > 0 ? ((att.Presents / att.TotalClasses) * 100).toFixed(2) : 0,
            topicName: att.TopicName || 'N/A'
        })),
        // Date info
        currentDate: new Date().toLocaleDateString(),
        currentYear: new Date().getFullYear(),
        // Keep original structure for advanced templates
        _raw: student
    };
};
/**
 * Pagination rules for every rendered report, print and PDF alike.
 *
 * Chrome will happily end a page immediately after a table's <thead>, or after
 * its first row, leaving a bare header strip at the foot of one page and the
 * data on the next. Templates live in the database and differ per tenant, so
 * this is fixed here — one place that every preview, download and certified
 * PDF passes through — rather than by editing template rows.
 *
 * The rule that does the real work is `break-before: avoid` on the first two
 * body rows: a break may not fall between the header and row 1, nor between
 * rows 1 and 2, so if two rows cannot fit under the header the break is pushed
 * up and the whole block starts the next page. The same applied to the last two
 * rows stops a single row being widowed onto a page of its own.
 *
 * Deliberately NOT `break-inside: avoid` on the table itself — that would make
 * long tables unbreakable and Chrome would either overflow them or waste most
 * of a page. Verified against Puppeteer's Chrome: long tables still split
 * normally and repeat their header on each page.
 */
/**
 * Bind each test-category label to the table it introduces.
 *
 * The DB-held grade cards write a category as its own <tr> holding a bold
 * <span>, with the category's table in the NEXT <tr>. Nothing ties the two
 * together, so when the table was too tall for the remaining space Chrome
 * started it on the next page and left the label stranded at the foot of the
 * previous one — 62 stranded labels in a 79-page batch dossier.
 *
 * CSS alone cannot fix this here. `break-after: avoid` is ignored on <tr>, and
 * ignored again on block boxes inside a <td>, because a table cell is its own
 * fragmentation context — and these cards are nested tables all the way down.
 * The one construct Chrome honours is <thead>: it is kept with the table across
 * a break and repeated on continuation pages.
 *
 * So the label is promoted into the table's own <thead>, together with the
 * existing <th> header bands. Measured on a real 65-student dossier: stranded
 * labels 62 -> 0.
 *
 * Idempotent and conservative: a table that already has a <thead> is skipped,
 * as is any candidate row that holds a table or has no label text.
 */
const bindCategoryLabelsToTables = (html) => {
    if (typeof DOMParser === 'undefined')
        return html; // SSR / non-browser caller
    try {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        doc.querySelectorAll('tr').forEach((tr) => {
            const next = tr.nextElementSibling;
            if (!next || next.tagName !== 'TR')
                return;
            if (tr.querySelector('table'))
                return; // label rows hold no table
            const cell = next.querySelector(':scope > td');
            const table = cell?.querySelector(':scope > table');
            if (!cell || !table || table.tHead)
                return;
            const span = tr.querySelector('span');
            if (!span || !(span.textContent || '').trim())
                return;
            const widest = Math.max(...Array.from(table.rows).map((r) => Array.from(r.cells).reduce((acc, c) => acc + (c.colSpan || 1), 0)));
            const thead = doc.createElement('thead');
            const labelRow = doc.createElement('tr');
            const td = doc.createElement('td');
            td.colSpan = Number.isFinite(widest) && widest > 0 ? widest : 1;
            // Borderless so the label still reads as a heading above the table
            // rather than as a cell of it.
            td.setAttribute('style', 'border:none;padding:2px 0;text-align:left;');
            td.appendChild(span.cloneNode(true));
            labelRow.appendChild(td);
            thead.appendChild(labelRow);
            // Bring the existing header bands along so they stay under the label and
            // repeat with it.
            Array.from(table.rows)
                .filter((r) => r.querySelector('th'))
                .forEach((r) => thead.appendChild(r));
            table.insertBefore(thead, table.firstChild);
            tr.remove();
        });
        return doc.body.innerHTML;
    }
    catch {
        return html; // never let a presentation tweak break rendering
    }
};
exports.bindCategoryLabelsToTables = bindCategoryLabelsToTables;
exports.PRINT_PAGE_BREAK_CSS = `
  /* Long tables may still split across pages... */
  table { break-inside: auto; }
  /* ...but never through the middle of a row. */
  tr, th, td { break-inside: avoid; }
  /* Repeat column headers on every page a table continues onto. */
  thead { display: table-header-group; break-inside: avoid; break-after: avoid; }
  tfoot { display: table-footer-group; }
  /* Keep a header with at least two data rows, or move the block to the next
     page; likewise never strand the final row on its own. */
  tbody > tr:nth-child(-n+2) { break-before: avoid; }
  tbody > tr:nth-last-child(-n+2) { break-before: avoid; }
  /* Same guarantee for templates that write their header row as <tr><th> in
     the table body instead of wrapping it in <thead> — common in the
     database-held templates, and invisible to the thead rules above. Chained
     siblings rather than :nth-child so it still holds for the two-row headers
     (subject band over Max/SS band) these grade cards use. */
  tr:has(> th) + tr { break-before: avoid; }
  tr:has(> th) + tr + tr { break-before: avoid; }
  /* A section heading travels with the table it introduces. */
  h1, h2, h3, h4, h5, h6, caption { break-inside: avoid; break-after: avoid; }
  /* Category labels are bound to their table structurally instead — see
     bindCategoryLabelsToTables. break-before/after: avoid is ignored on <tr>
     and inside table cells, so CSS cannot do it here. */
`;
/**
 * `@media print` is what Puppeteer's page.pdf() and the browser's Print dialog
 * obey. No `@page { size }` here on purpose: the page size and orientation are
 * set by the caller (page.pdf's format/landscape), and a size declared in CSS
 * would silently override a landscape request the day anyone turns on
 * preferCSSPageSize.
 */
const printBreakStyleTag = () => `<style>\n@media print {\n${exports.PRINT_PAGE_BREAK_CSS}}\n</style>`;
/**
 * Put the pagination rules into an already-rendered document.
 *
 * Batch templates come back from Mustache as a whole document of the tenant's
 * own making, so the insertion point is discovered rather than assumed: last
 * thing in <head> when there is one, otherwise after <body>, otherwise the top
 * of the markup.
 *
 * Last in <head> means these rules win a tie against a template's own generic
 * `tr {}` / `table {}` declarations — deliberate, since this is a fix every
 * tenant should get. Every selector here is bare-element, so anything a
 * template scopes to a class still outranks it on specificity and keeps
 * working.
 */
const injectPrintBreakRules = (html) => {
    const style = printBreakStyleTag();
    if (/<\/head\s*>/i.test(html)) {
        return html.replace(/<\/head\s*>/i, `${style}</head>`);
    }
    const bodyOpen = html.match(/<body[^>]*>/i);
    if (bodyOpen) {
        return html.replace(bodyOpen[0], `${bodyOpen[0]}${style}`);
    }
    return `${style}${html}`;
};
/**
 * Render HTML by applying student data to template using Mustache
 * @param templateHtml - Mustache template HTML
 * @param students - Array of student data
 * @param batchStatistics - Optional batch-level statistics (for batch reports)
 * @param batchMetadata - Optional batch metadata (for batch reports)
 * @param isBatchReport - Whether this is a batch report or individual student reports
 */
const renderTemplateWithData = (templateHtml, students, batchStatistics, batchMetadata, isBatchReport = false, analyticsData, debugMode) => {
    try {
        console.log('[renderTemplateWithData] Rendering template with', students.length, 'students');
        console.log('[renderTemplateWithData] Batch report mode:', isBatchReport);
        // For batch reports, render single template with all students
        if (isBatchReport && batchMetadata) {
            console.log('[renderTemplateWithData] Batch statistics:', batchStatistics);
            console.log('[renderTemplateWithData] Analytics data:', analyticsData);
            console.log('[renderTemplateWithData] Batch metadata:', batchMetadata);
            // Create template context with batch data and student array
            const templateContext = {
                // Batch-level data
                ...batchMetadata,
                ...batchStatistics,
                ...analyticsData,
                // Students array for iteration in template
                students: students,
                // Debug mode for info tooltips
                ...(debugMode && { debugMode: true }),
            };
            console.log('[renderTemplateWithData] Rendering batch template with context');
            const rendered = mustache_1.default.render(templateHtml, templateContext);
            // Batch templates are returned as-is apart from this: the tenant's markup
            // owns the document, so the pagination rules are injected into it rather
            // than the document being rebuilt around them.
            return injectPrintBreakRules(rendered);
        }
        // For individual student reports, render template for each student and concatenate
        const renderedStudents = students.map((student, index) => {
            // Transform student data into flat structure
            const transformedData = transformStudentData(student);
            if (transformedData.tests && transformedData.tests.length > 0) {
                console.log('[renderTemplateWithData] First test sections:', transformedData.tests[0].sections?.length || 0);
            }
            const rendered = mustache_1.default.render(templateHtml, { ...transformedData, ...(debugMode && { debugMode: true }) });
            // Keep each test-category label with its table across page breaks.
            return (0, exports.bindCategoryLabelsToTables)(rendered);
        });
        // Combine all rendered HTML with page breaks
        const combinedHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Grade Report</title>
        <style>
          /* Page break for print/PDF */
          .student-report {
            page-break-after: always;
            page-break-inside: avoid;
            margin-bottom: 20px;
          }
          .student-report:last-child {
            page-break-after: auto;
          }
          /* Common styles */
          body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 0;
          }
          @media print {
            .student-report {
              page-break-after: always;
              page-break-inside: avoid;
            }
            .student-report:last-child {
              page-break-after: auto;
            }
            /* A student whose card runs past one page still breaks — the rule
               above is only a hint — so the table rules below decide where. */
${exports.PRINT_PAGE_BREAK_CSS}
          }
        </style>
      </head>
      <body>
        ${renderedStudents.map((html, index) => `
          <div class="student-report" data-student-index="${index}">
            ${html}
          </div>
        `).join('')}
      </body>
      </html>
    `;
        return combinedHtml;
    }
    catch (error) {
        console.error('[renderTemplateWithData] Error rendering template:', error);
        return '<html><body><h1>Error rendering template</h1><p>' + error.message + '</p></body></html>';
    }
};
exports.renderTemplateWithData = renderTemplateWithData;
/**
 * Preview HTML in a new window
 */
const previewHTMLInNewWindow = (html, title = 'Preview') => {
    const previewWindow = window.open('', '_blank');
    if (previewWindow) {
        previewWindow.document.write(html);
        previewWindow.document.title = title;
        previewWindow.document.close();
    }
    else {
        alert('Please allow popups to preview the report');
    }
};
exports.previewHTMLInNewWindow = previewHTMLInNewWindow;
/**
 * Generate PDF from HTML using server-side Puppeteer.
 * Sends the rendered HTML to nodefork's /reports/html-to-pdf endpoint,
 * which uses Puppeteer (headless Chrome) for pixel-perfect PDF rendering.
 */
const generatePDFFromHTML = async (html, filename = 'report.pdf', options) => {
    try {
        console.log('[generatePDFFromHTML] Generating PDF via server:', filename);
        const url = `${(0, config_1.getConfig)().nodeSiteUrl}v1/reports/html-to-pdf`;
        const response = await fetch(url, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({ html, filename, options }),
        });
        if (!response.ok) {
            const errorText = await response.text();
            console.error('[generatePDFFromHTML] Server error:', errorText);
            throw new Error(`PDF generation failed: ${response.statusText}`);
        }
        // Download the PDF blob
        const blob = await response.blob();
        const downloadUrl = URL.createObjectURL(blob);
        // Trigger browser download
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(downloadUrl);
        console.log('[generatePDFFromHTML] PDF downloaded successfully');
    }
    catch (error) {
        console.error('[generatePDFFromHTML] Error generating PDF:', error);
        throw error;
    }
};
exports.generatePDFFromHTML = generatePDFFromHTML;
/**
 * Full flow: Fetch template, render with data, and preview HTML
 */
const previewReportHTML = async (templateId, students, filters, refreshRunId) => {
    try {
        const debugMode = localStorage.getItem('debugMode') === 'true'
            || sessionStorage.getItem('demoMode') === 'true';
        const templateData = await (0, exports.fetchTemplateWithData)(templateId, students, filters, debugMode, refreshRunId, 'PREVIEW_HTML');
        if (!templateData) {
            throw new Error('Failed to fetch template data');
        }
        const renderedHtml = (0, exports.renderTemplateWithData)(templateData.templateHtml, templateData.students, // Use enriched students from backend
        templateData.batchStatistics, templateData.batchMetadata, templateData.isBatchReport, templateData.analyticsData, debugMode);
        (0, exports.previewHTMLInNewWindow)(renderedHtml, `Preview: ${templateData.templateName}`);
    }
    catch (error) {
        console.error('[previewReportHTML] Error:', error);
        alert('Failed to preview report: ' + error.message);
    }
};
exports.previewReportHTML = previewReportHTML;
/**
 * Full flow: Fetch template, render with data, and generate PDF
 */
const generateReportPDF = async (templateId, students, filename, filters, refreshRunId) => {
    try {
        const debugMode = localStorage.getItem('debugMode') === 'true'
            || sessionStorage.getItem('demoMode') === 'true';
        const templateData = await (0, exports.fetchTemplateWithData)(templateId, students, filters, debugMode, refreshRunId, 'PDF');
        if (!templateData) {
            throw new Error('Failed to fetch template data');
        }
        const renderedHtml = (0, exports.renderTemplateWithData)(templateData.templateHtml, templateData.students, // Use enriched students from backend
        templateData.batchStatistics, templateData.batchMetadata, templateData.isBatchReport, templateData.analyticsData, debugMode);
        const pdfFilename = filename || `${templateData.templateName}-${new Date().toISOString().split('T')[0]}.pdf`;
        await (0, exports.generatePDFFromHTML)(renderedHtml, pdfFilename);
    }
    catch (error) {
        console.error('[generateReportPDF] Error:', error);
        alert('Failed to generate PDF: ' + error.message);
    }
};
exports.generateReportPDF = generateReportPDF;
/**
 * Excel sibling of previewReportHTML / generateReportPDF.
 *
 * Calls the server-side export-xlsx endpoint, which runs the *same*
 * enrichment pipeline as get-template-with-data (same filter handling,
 * same scalar precedence, same provenance ids) — then emits a workbook
 * the campus team can use to validate "the numbers parents see."
 *
 * Returns the X-Dossier-Id from the response so the caller can confirm
 * which audit row (D-…) the export was tied to.
 */
const exportReportExcel = async (templateId, templateName, students, filters, refreshRunId) => {
    const url = `${(0, config_1.getConfig)().nodeSiteUrl}v1/reports/get-template-with-data/export-xlsx`;
    const payload = {
        templateId,
        templateName,
        students,
        ...(filters && { filters }),
        ...(typeof refreshRunId === 'number' && { refreshRunId }),
    };
    const response = await fetch(url, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
    });
    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Excel export failed: ${response.status} ${errorText.slice(0, 200)}`);
    }
    const dossierId = response.headers.get('x-dossier-id');
    const blob = await response.blob();
    // Pull filename from Content-Disposition if present
    const cd = response.headers.get('content-disposition') || '';
    const filenameMatch = cd.match(/filename="([^"]+)"/);
    const safeTemplate = (templateName || 'dossier').replace(/[^a-zA-Z0-9_-]+/g, '_').slice(0, 60);
    const fallbackFilename = `${safeTemplate}_${dossierId || 'export'}_${new Date().toISOString().split('T')[0]}.xlsx`;
    const filename = filenameMatch ? filenameMatch[1] : fallbackFilename;
    // Trigger browser download
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(downloadUrl);
    return { dossierId };
};
exports.exportReportExcel = exportReportExcel;
